/** @internal – import from `@/store/useDepartmentStore` instead. */
import { runSSEStream } from "../sseRunner";
import { buildErrorMessage } from "../utils";
import { sseEvents } from "../../../events";

export interface EcommerceMetricsInput {
  storeId: string;
  connectionMethod: "shopify" | "manual";
  businessName: string;
  industry?: string;
  averageOrderValue?: number;
  monthlyRevenue?: number;
  totalAdSpend?: number;
  totalCustomers?: number;
}

/**
 * Ecommerce-metrics SSE pipeline runner. Posts to
 * `/core/coagent/ecommerce-metrics` and publishes typed
 * `ecommerce-metrics:*` events.
 *
 * Migrated off the `SSECallbacks` mutator-bag in Phase 8 commit 77.
 */
export async function runEcommerceMetricsSSE(
  metricsInput: EcommerceMetricsInput,
  sessionId: string,
  signal?: AbortSignal,
): Promise<void> {
  await runSSEStream(metricsInput, {
    pipeline: "ecommerce-metrics",
    endpoint: "/core/coagent/ecommerce-metrics",
    sessionId,
    buildBody: (sid, input) => ({ sessionId: sid, ...input }),
    errorPrefix: "E-commerce metrics",
    signal,
    debugMeta: { storeId: metricsInput.storeId },
    handleEvent: (event) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { phase, status, data, error, details } = event as Record<string, any>;
      const ev = data as Record<string, unknown> | undefined;

      if (phase === "metrics_calculation" && status === "running") {
        sseEvents.publish({
          type: "ecommerce-metrics:metrics-calculation-running",
          pipeline: "ecommerce-metrics",
          sessionId,
        });
        return;
      }

      if (phase === "metrics_calculation" && status === "complete") {
        sseEvents.publish({
          type: "ecommerce-metrics:metrics-calculation-complete",
          pipeline: "ecommerce-metrics",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "priority_scoring" && status === "running") {
        sseEvents.publish({
          type: "ecommerce-metrics:priority-scoring-running",
          pipeline: "ecommerce-metrics",
          sessionId,
        });
        return;
      }

      if (phase === "priority_scoring" && status === "complete") {
        sseEvents.publish({
          type: "ecommerce-metrics:priority-scoring-complete",
          pipeline: "ecommerce-metrics",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "ad_cost_estimation" && status === "running") {
        sseEvents.publish({
          type: "ecommerce-metrics:ad-cost-estimation-running",
          pipeline: "ecommerce-metrics",
          sessionId,
        });
        return;
      }

      if (phase === "ad_cost_estimation" && status === "complete") {
        sseEvents.publish({
          type: "ecommerce-metrics:ad-cost-estimation-complete",
          pipeline: "ecommerce-metrics",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "ad_cost_synthesis" && status === "running") {
        sseEvents.publish({
          type: "ecommerce-metrics:ad-cost-synthesis-running",
          pipeline: "ecommerce-metrics",
          sessionId,
        });
        return;
      }

      if (phase === "ad_cost_synthesis" && status === "complete") {
        sseEvents.publish({
          type: "ecommerce-metrics:ad-cost-synthesis-complete",
          pipeline: "ecommerce-metrics",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "done") {
        sseEvents.publish({
          type: "ecommerce-metrics:done",
          pipeline: "ecommerce-metrics",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "error") {
        const message = buildErrorMessage(error, details, "E-commerce metrics pipeline failed");
        console.error("[coagent/ecommerce-metrics] SSE error", { message, details });
        sseEvents.publish({
          type: "ecommerce-metrics:error",
          pipeline: "ecommerce-metrics",
          sessionId,
          message,
          ...(details !== undefined ? { details } : {}),
        });
      }
    },
  });
}
